import { useLocalStorage } from 'usehooks-ts';
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from 'axios';
import { useEffect } from 'react';
import {
  ACCESS_TOKEN_KEY,
  BASE_URL,
  RECRUITER_DEACTIVATED,
  REFRESH_TOKEN_EXPIRED,
  REFRESH_TOKEN_KEY,
  USER_UNAUTHORIZED,
} from '../../constants/config.ts';
import useLogout from '../useLogout.ts';

let isRefreshing = false;

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  retryAttempt?: boolean;
}

const useAxiosAuthSetup = (instance: AxiosInstance) => {
  const [logout] = useLogout(true);

  const [, setAccessToken] = useLocalStorage(ACCESS_TOKEN_KEY, '');
  const [, setRefreshToken] = useLocalStorage(REFRESH_TOKEN_KEY, '');

  const sendRefresh = async (refreshToken: string) => {
    isRefreshing = true;

    try {
      const { data } = await axios.post(`${BASE_URL}auth/refresh/`, {
        refreshToken,
      });

      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);

      return data;
    } finally {
      isRefreshing = false;
    }
  };

  // Ensure Promise rejection with an Error object
  const rejectWithError = (err: unknown) => {
    if (err instanceof Error) {
      return Promise.reject(err);
    }
    const errorMessage = err instanceof Object ? JSON.stringify(err) : String(err);
    return Promise.reject(new Error(errorMessage));
  };

  // Handle request errors with proper Error objects
  const handleRequestError = (error: unknown) => rejectWithError(error);

  // Handle token refresh when unauthorized
  const handleUnauthorizedError = async (
    err: AxiosError,
    originalConfig: ExtendedAxiosRequestConfig,
  ) => {
    if (isRefreshing) {
      return waitForRefreshToComplete(originalConfig);
    }

    // Create a new config for retry attempt
    const config = {
      ...originalConfig,
      retryAttempt: true,
    };

    try {
      const refreshToken = JSON.parse(localStorage.getItem(REFRESH_TOKEN_KEY) || '"null"');
      if (!refreshToken) {
        return logout();
      }

      const data = await sendRefresh(refreshToken);
      return await retryRequestWithNewToken(config, data.accessToken);
    } catch (refreshError) {
      return handleRefreshError(refreshError, err);
    }
  };

  // Wait for an ongoing refresh to complete before continuing
  const waitForRefreshToComplete = (
    config: ExtendedAxiosRequestConfig,
  ) => new Promise((resolve) => {
    const intervalId = setInterval(() => {
      if (!isRefreshing) {
        clearInterval(intervalId);
        resolve(instance(config));
      }
    }, 100);
  });

  // Retry the original request with a new token
  const retryRequestWithNewToken = async (
    config: ExtendedAxiosRequestConfig,
    accessToken: string,
  ) => {
    const headers = new AxiosHeaders(config.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);

    const newRequestConfig: ExtendedAxiosRequestConfig = {
      ...config,
      headers,
    };

    return instance(newRequestConfig);
  };

  // Handle errors that occur during token refresh
  const handleRefreshError = (refreshError: unknown, originalError: AxiosError) => {
    if (
      refreshError instanceof AxiosError &&
      refreshError.response?.status === REFRESH_TOKEN_EXPIRED &&
      originalError.response?.data
    ) {
      logout();
    }

    return rejectWithError(refreshError);
  };

  useEffect(() => {
    // Setup request interceptor
    instance.interceptors.request.use(
      (config) => {
        const token = JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY) || '{}');
        if (token) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      handleRequestError,
    );

    // Setup response interceptor
    instance.interceptors.response.use(
      (res) => res,
      async (err: AxiosError) => {
        const originalConfig = err.config as ExtendedAxiosRequestConfig | undefined;
        if (!originalConfig) {
          return rejectWithError(err);
        }

        // Handle specific error status codes
        if (!err.response) {
          return rejectWithError(err);
        }

        const { status } = err.response;

        // Handle deactivated recruiter
        if (status === RECRUITER_DEACTIVATED) {
          logout();
          return rejectWithError(err);
        }

        // Handle unauthorized with retry
        if (status === USER_UNAUTHORIZED && !originalConfig.retryAttempt) {
          return handleUnauthorizedError(err, originalConfig);
        }

        // Handle forbidden
        if (status === 403 && err.response.data) {
          return rejectWithError(err);
        }

        return rejectWithError(err);
      },
    );
  }, []);
};

export default useAxiosAuthSetup;
